/* eslint-disable react/no-array-index-key */
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useContextMatchParams } from 'contexts/matchParams';
import { useContextResultOrigin } from 'contexts/resultOrigin';
import { useContextProperties } from 'contexts/properties';

import { getPlace } from 'dataProvider';

import { useContextCurrentSite } from 'contexts';
import LoadingPlaceholder from 'components/loadingPlaceholder';
import usePlacesProvider from 'dataProvider/hooks/usePlacesProvider';
import BreadCrumb from 'components/breadcrumb';
import useDebounce from 'dataProvider/hooks/useDebounce';
import useResultProvider from 'dataProvider/hooks/useResultProvider';

import { TYPES, SUBTYPES } from '../../envs';

import CustomMap from '../../components/homeMap/customMap';
import AtresBarChart from '../../components/charts/atresBarChart';
import AtresPieChart from '../../components/charts/atresPieChart';
import TableCompare from '../../components/table';
import Tag from '../../components/tag';
import Tags from '../../components/tags';
import ElTabs from '../../components/elTabs';
import Autocomplete from '../../components/autocomplete';
import TableScrutine from '../../components/tableScrutiny';

import { getSite, LOCALHOST } from '../../constants/sites';

import { PactometroCheckSimple } from '../../components';

/**
 * Componente que va a pintar el Home principal de las elecciones municipales
 *
 *
 */
const MainMunicipal = ({
  dataResult,
  oldData,
  onChangeLocation,
  selectedPlace,
  provincePlace
}) => {
  const { matchParams } = useContextMatchParams();
  const {
    chartType,
    community,
    country,
    electionNumber,
    province,
    town,
    type,
    subtype,
    year
  } = matchParams;
  const resultOrigin = useContextResultOrigin();
  const { params } = useContextProperties();

  const [autocomplePro, setAutocomplePro] = useState('');
  const [autocompleMun, setAutocompleMun] = useState('');

  const Chart = chartType === 'arc' ? AtresPieChart : AtresBarChart;
  const site = getSite();

  const { currentSite } = useContextCurrentSite();

  const { rows } =
    params.sites[site === LOCALHOST ? currentSite : site].views[
      type.toLocaleLowerCase()
    ].home;

  const { result: partiesWins } = useResultProvider({
    active: resultOrigin.primary?.active,
    year,
    type,
    subtype,
    placeId: country,
    electionNumber,
    details: Boolean(town),
    site,
    totals: true
  });
  const partiesWinners = partiesWins[country] || partiesWins;

  const [mapData, setMapData] = useState([]);
  useEffect(() => {
    Promise.all([
      getPlace({
        filter: `typeplace=PROVINCE&size=60`,
        type,
        electionType: type
      }), // provincias de españa
      getPlace({
        filter: `typeplace=TOWN&size=60&sortby=main`,
        type,
        electionType: type
      }) // capitales provincias de españa
    ]).then(([provinces, provincesCapital]) => {
      const mappedProvinces = provinces.content.map(p => {
        const capital = provincesCapital.content.find(
          c => c.parentId === p.ineId
        );
        return {
          ...p,
          capital
        };
      });

      setMapData(mappedProvinces);
    });
  }, [electionNumber, type, year, community]);

  const { result: dataPlacesProvince } = usePlacesProvider({
    type: 'PROVINCE',
    sortby: 'name',
    parentId: community,
    electionType: type
  });

  const debouncedSearch = useDebounce(autocompleMun, 400);

  const { result: dataPlacesTown } = usePlacesProvider({
    sortby: 'name',
    name: debouncedSearch,
    type: 'TOWN',
    parentId: provincePlace?.ineId || '',
    size: 10,
    electionType: type
  });

  const { result: dataPlacesTownPrincipals } = usePlacesProvider({
    sortby: 'main',
    parentId: province,
    type: 'TOWN',
    grandParentId: province ? null : community,
    size: 10,
    electionType: type
  });

  const dataBreadcrumb = [
    {
      name: 'Municipales',
      ineId: 'C108'
    },
    selectedPlace
  ];

  if (
    !rows ||
    !dataResult ||
    !oldData ||
    !dataPlacesProvince ||
    !dataPlacesTown ||
    !dataPlacesTownPrincipals
  ) {
    return <LoadingPlaceholder />;
  }
  const content = rows.map((row, index) => (
    <div className={row.class} key={`row-${index}`}>
      {row.columns.map((column, index2) => (
        <div className={column.class} key={`col-${index2}`}>
          {column.components.map(item => (
            <div className={item.class || ''} key={item.module}>
              {/* PlacesProvider REGION PROVINCE TOWN */}
              {item.module === 'SELECTORS' && (
                <Fragment>
                  <div className="row-places">
                    {
                      <>
                        {/* PlacesProvider PROVINCE */}
                        {dataPlacesProvince && (
                          <div className="col-places">
                            <Autocomplete
                              results={dataPlacesProvince.filter(d =>
                                autocomplePro
                                  ? d.name
                                      .toLocaleLowerCase()
                                      .includes(
                                        autocomplePro.toLocaleLowerCase()
                                      )
                                  : d
                              )}
                              onChange={value => setAutocomplePro(value)}
                              onSelect={(event, select) => {
                                onChangeLocation(event, select);

                                setAutocomplePro('');
                              }}
                              placeholder="Buscar provincia"
                              showArrow
                              defaultValue={selectedPlace.parentId}
                              type={type}
                              params={params}
                              year={year}
                            />
                          </div>
                        )}
                        {/* PlacesProvider TOWN */}
                        {dataPlacesTown && (
                          <div className="col-places">
                            <Autocomplete
                              results={dataPlacesTown}
                              onChange={value => setAutocompleMun(value)}
                              onSelect={(event, select) => {
                                onChangeLocation(event, select);

                                setAutocompleMun('');
                              }}
                              placeholder="Buscar municipio"
                              showArrow
                              type={type}
                              params={params}
                              year={year}
                            />
                          </div>
                        )}
                      </>
                    }
                  </div>
                  <BreadCrumb dataBreadcrumb={dataBreadcrumb} />
                </Fragment>
              )}
              {item.module === 'RESULTADOS' && (
                <Chart
                  data={dataResult.partiesResult || []}
                  dataKey="seats"
                  isSenate={subtype === SUBTYPES.SENATE}
                  showMajorityLine={
                    type !== TYPES.REGIONAL
                      ? !province && !community
                      : !province
                  }
                  showLabel
                  unitLabel={null}
                  labelAbrv=""
                  seatTotal={dataResult.totalSeats || 0}
                  type={type}
                />
              )}

              {/* ResultProvider OLDATA historic */}
              {item.module === 'RESULTADOS_DOBLES' && (
                <div
                  className={
                    selectedPlace && selectedPlace.typePlace === 'PROVINCE'
                      ? 'provincia'
                      : ''
                  }
                >
                  <Chart
                    chartInfo1={{
                      innerRadius: 122,
                      outerRadius: 165,
                      label: year
                    }}
                    chartInfo2={{
                      innerRadius: 90,
                      outerRadius: 120,
                      label: resultOrigin.historic.year
                    }}
                    data={dataResult.partiesResult || []}
                    data2={oldData.partiesResult || []}
                    dataKey="seats"
                    isSenate={subtype === SUBTYPES.SENATE}
                    showMajorityLine
                    showLabel
                    unitLabel={null}
                    labelAbrv=""
                    seatTotal={dataResult.totalSeats || 0}
                    type={type}
                  />
                </div>
              )}
              {/* ResultProvider OLDATA historic */}
              {item.module === 'HOME_DOBLE' && (
                <Chart
                  data={dataResult.partiesResult || []}
                  data2={oldData.partiesResult || []}
                  dataKey="seats"
                  isSenate={subtype === SUBTYPES.SENATE}
                  showMajorityLine={
                    type !== TYPES.REGIONAL
                      ? !province && !community
                      : !province
                  }
                  showLabel
                  unitLabel={null}
                  labelAbrv=""
                  seatTotal={oldData.totalSeats || 0}
                  type={type}
                />
              )}
              {/* ResultProvider OLDATA historic */}
              {item.module === 'MAP' && (
                <CustomMap
                  selectedPlace={selectedPlace}
                  partiesWinners={partiesWinners || dataResult.partiesWinners}
                  partiesWinnersNoFlat={
                    partiesWinners || dataResult.partiesWinners
                  }
                  native
                  data={dataResult}
                  oldData={oldData}
                  year={year}
                  electionNumber={electionNumber}
                  type={type}
                  placeId={selectedPlace.ineId}
                  maps={mapData}
                  onChange={(event, _item) =>
                    onChangeLocation(event, _item, true)
                  }
                  currentCommunity=""
                  showTooltipInfo
                  showDots
                />
              )}
              {item.module === 'TABS_CONTAINER' && (
                <ElTabs
                  titles={item.data.tabs.map(tab =>
                    tab.title === 'PREV_YEAR'
                      ? oldData.year?.toString()
                      : tab.title
                  )}
                >
                  {item.data.tabs.map(tab => (
                    <Fragment key={tab.title}>
                      {tab.component === 'TABLE' && (
                        <TableCompare
                          className="current"
                          data={dataResult}
                          oldData={oldData}
                          year={year}
                          electionNumber={electionNumber}
                          electionType={type}
                          placeId={selectedPlace.ineId}
                          details={
                            subtype === SUBTYPES.CONGRESS || TYPES.MUNICIPAL
                          }
                          isSenate={subtype === SUBTYPES.SENATE}
                          type={type}
                        />
                      )}
                      {tab.component === 'RESULTADOS_PREVIOS' && (
                        <div>
                          <div className="graph_with_leyend">
                            <Chart
                              data={
                                // (tab.data.year === params.year
                                //   ? dataResult.partiesResult
                                //   : oldData.partiesResult) || []
                                oldData.partiesResult || []
                              }
                              dataKey="seats"
                              isSenate={subtype === SUBTYPES.SENATE}
                              showMajorityLine={
                                type !== TYPES.REGIONAL
                                  ? !province && !community
                                  : !province
                              }
                              showLabel
                              labelAbrv=""
                              seatTotal={dataResult.totalSeats || 0}
                              unitLabel={null}
                              type={type}
                            />
                          </div>
                        </div>
                      )}

                      {tab.component === 'PACTOS' && (
                        <div className="pactometroWrapper">
                          <div className="pactometroScroll">
                            <PactometroCheckSimple
                              totalSeat={dataResult.totalSeats}
                              seatData={dataResult.partiesResult || []}
                              type={type}
                            />
                          </div>
                        </div>
                      )}
                    </Fragment>
                  ))}
                </ElTabs>
              )}
              {item.module === 'TABLE_SCRUTINE' && (
                <TableScrutine
                  totalVotes={dataResult?.totalVotes?.toLocaleString('de-DE')}
                  invalidVotes={dataResult?.invalidVotes?.toLocaleString(
                    'de-DE'
                  )}
                  whiteVotes={dataResult?.whiteVotes?.toLocaleString('de-DE')}
                  abstentionVotes={dataResult?.abstentionVotes?.toLocaleString(
                    'de-DE'
                  )}
                  invalidPercent={dataResult?.invalidPercent}
                  whitePercent={dataResult?.whitePercent}
                  abstentionPercent={dataResult?.abstentionPercent}
                  participationPercent={dataResult?.participationPercent}
                  currentCountPercent={dataResult?.currentCountPercent}
                  isActive={resultOrigin?.primary?.active}
                  difference={
                    Number(dataResult.participationPercent) -
                    Number(oldData.participationPercent)
                  }
                />
              )}
              {/* PlacesProvider TOWN */}
              {item.module === 'TAGS' && (
                <Fragment>
                  <h3 className="tags-title">Resultado de las elecciones en</h3>
                  <div className="tags_container">
                    <Tags>
                      {dataPlacesTownPrincipals?.map(d => (
                        <Tag
                          key={`${d.ineId}-${d.name}`}
                          onClick={e => {
                            onChangeLocation(e, d);
                          }}
                          native
                          route={d.path}
                        >
                          {d.name}
                        </Tag>
                      ))}
                    </Tags>
                  </div>
                </Fragment>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  ));

  return <Fragment>{content}</Fragment>;
};

export default MainMunicipal;

MainMunicipal.propTypes = {
  onChangeLocation: PropTypes.func,
  year: PropTypes.string,
  provincePlace: PropTypes.shape({
    name: PropTypes.string,
    typePlace: PropTypes.string,
    ineId: PropTypes.string,
    parentId: PropTypes.string
  }),
  dataResult: PropTypes.shape({
    abstentionPercent: PropTypes.string,
    abstentionVotes: PropTypes.number,
    currentCountPercent: PropTypes.string,
    invalidPercent: PropTypes.string,
    invalidVotes: PropTypes.number,
    participationPercent: PropTypes.string,
    partiesResult: PropTypes.arrayOf(PropTypes.shape({})),
    partiesWinners: PropTypes.arrayOf(PropTypes.shape({})),
    totalSeats: PropTypes.number,
    totalVotes: PropTypes.number,
    whitePercent: PropTypes.string,
    whiteVotes: PropTypes.number
  }),
  selectedPlace: PropTypes.shape({
    name: PropTypes.string,
    typePlace: PropTypes.string,
    ineId: PropTypes.string,
    parentId: PropTypes.string
  }),
  partiesWinners: PropTypes.arrayOf(PropTypes.shape({})),
  resultOrigin: PropTypes.shape({
    historic: PropTypes.shape({
      year: PropTypes.number,
      partiesResult: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    primary: PropTypes.shape({
      active: PropTypes.bool,
      partiesResult: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }),
  oldData: PropTypes.shape({
    abstentionPercent: PropTypes.string,
    abstentionVotes: PropTypes.number,
    currentCountPercent: PropTypes.string,
    invalidPercent: PropTypes.string,
    invalidVotes: PropTypes.number,
    participationPercent: PropTypes.string,
    partiesResult: PropTypes.arrayOf(PropTypes.shape({})),
    totalSeats: PropTypes.number,
    totalVotes: PropTypes.number,
    whitePercent: PropTypes.string,
    whiteVotes: PropTypes.number,
    year: PropTypes.string
  })
};

MainMunicipal.defaultProps = {
  dataResult: {},
  oldData: {},
  onChangeLocation: () => {},
  partiesWinners: {},
  provincePlace: {},
  resultOrigin: {},
  selectedPlace: {},
  year: ''
};
